/**
 * VÉRAULT TRAVELS OS - Manage Section Pages
 * Deep-linked folders: Trips, Bookings, Clients, Payments, Invoices, Forms
 * FULLY OPERATIONAL — all sections wired to backend CRUD APIs
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Plane, Hotel, Car, Calendar, Users,
  DollarSign, FileText, Send, ChevronRight, Plus, Loader2,
  CreditCard, ClipboardList, Filter, Check, AlertCircle, Trash2,
  Edit2, Eye, ArrowUpRight, ArrowDownLeft, Utensils, Shield, MoreHorizontal, Sparkles
} from 'lucide-react';
import { formatDate, getTripStartDate } from '../utils/dateFormat';
import { BookingFormModal } from '../components/BookingFormModal';
import EmailParserModal from '../components/EmailParserModal';
import { PaymentFormModal } from '../components/PaymentFormModal';
import { ClientFormModal } from '../components/ClientFormModal';
import { MasterClientProfile } from '../components/MasterClientProfile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Route name mapping for dynamic labels
const ROUTE_LABELS = {
  '/advisor/manage': 'Manage',
  '/advisor/manage/trips': 'Trips',
  '/advisor/manage/bookings': 'Bookings',
  '/advisor/manage/clients': 'Clients',
  '/advisor/manage/payments': 'Payments',
  '/advisor/manage/invoices': 'Invoices',
  '/advisor/manage/forms': 'Forms',
  '/advisor': 'Dashboard',
};

// Parent route mapping for proper navigation hierarchy
const PARENT_ROUTES = {
  '/advisor/manage/trips': '/advisor/manage',
  '/advisor/manage/bookings': '/advisor/manage',
  '/advisor/manage/clients': '/advisor/manage',
  '/advisor/manage/payments': '/advisor/manage',
  '/advisor/manage/invoices': '/advisor/manage',
  '/advisor/manage/forms': '/advisor/manage',
  '/advisor/manage': '/advisor',
};

// ==================== SMART BACK BUTTON COMPONENT ====================
export function BackButton() {
  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;
  const parentRoute = PARENT_ROUTES[currentPath] || '/advisor';
  const parentLabel = ROUTE_LABELS[parentRoute] || 'Dashboard';

  const handleBack = () => {
    if (window.history.length > 2) {
      const referrer = document.referrer;
      if (referrer && referrer.includes(window.location.origin)) {
        navigate(-1);
        return;
      }
    }
    navigate(parentRoute);
  };

  return (
    <button
      onClick={handleBack}
      className="flex items-center gap-2 text-[var(--vt-text-muted)] hover:text-[var(--vt-text)] transition-colors mb-4 font-body text-sm"
      data-testid="back-btn"
    >
      <ArrowLeft className="w-4 h-4" />
      Return to {parentLabel}
    </button>
  );
}

// ==================== TRIPS PAGE ====================
export function TripsPage() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => { fetchTrips(); }, []);

  const fetchTrips = async () => {
    try {
      setLoading(true);
      // Use dashboard endpoint to get trips WITH financial health
      const res = await axios.get(`${API}/travels/dashboard`);
      setTrips(res.data?.trips_in_progress || []);
    } catch (err) {
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTrip = async (e, tripId, tripName) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${tripName}"? This cannot be undone.`)) return;
    setDeletingId(tripId);
    try {
      await axios.delete(`${API}/travels/trips/${tripId}`);
      toast.success('Trip deleted');
      fetchTrips();
    } catch (err) {
      toast.error('Failed to delete trip');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredTrips = trips.filter(trip => {
    if (filterStatus !== 'all' && trip.trip_status !== filterStatus) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (trip.internal_trip_name?.toLowerCase().includes(q)) ||
      (trip.destinations?.toLowerCase().includes(q)) ||
      (trip.client_name?.toLowerCase().includes(q))
    );
  });

  return (
    <div className="min-h-screen p-6 md:p-12" style={{ backgroundColor: 'var(--vt-bg)' }}>
      <div className="max-w-6xl mx-auto">
        <BackButton />
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-2xl text-[var(--vt-text)]">Trips</h1>
            <p className="text-sm text-[var(--vt-text-muted)]">{trips.length} total trips</p>
          </div>
        </div>

        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vt-text-muted)]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search trips..."
              className="pl-10 bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
              data-testid="trip-search"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[160px] bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="trip-status-filter">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="booking_in_progress">Booking</SelectItem>
              <SelectItem value="booked">Booked</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--vt-gold)]" />
          </div>
        ) : (
          <div className="space-y-2">
            {filteredTrips.map(trip => (
              <div key={trip.id} className="group p-4 bg-[var(--vt-bg-card)] rounded-xl border border-[var(--vt-border)] hover:border-[var(--vt-gold)] transition-all">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => navigate(`/advisor/trips/${trip.id}`)}
                    className="text-left flex-1"
                    data-testid={`trip-row-${trip.id}`}
                  >
                    <p className="font-medium text-[var(--vt-text)]">
                      {trip.internal_trip_name || trip.destinations}
                    </p>
                    <p className="text-sm text-[var(--vt-text-muted)]">
                      {trip.client_name} {getTripStartDate(trip) ? `\u2022 ${formatDate(getTripStartDate(trip))}` : ''}
                    </p>
                  </button>
                  <div className="flex items-center gap-3">
                    {/* Financial Health Indicator */}
                    {trip.financial_health && trip.financial_health.status !== 'no_data' && (
                      <div
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
                          trip.financial_health.status === 'critical'
                            ? 'bg-red-50 text-red-700'
                            : trip.financial_health.status === 'warning'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-green-50 text-green-700'
                        }`}
                        title={trip.financial_health.flags?.join(', ') || 'Healthy'}
                        data-testid={`trip-health-${trip.id}`}
                      >
                        <div className={`w-1.5 h-1.5 rounded-full ${
                          trip.financial_health.status === 'critical' ? 'bg-red-500' :
                          trip.financial_health.status === 'warning' ? 'bg-amber-500' : 'bg-green-500'
                        }`} />
                        {trip.financial_health.status === 'critical' ? 'Overdue' :
                         trip.financial_health.status === 'warning' ? 'Attention' : 'On Track'}
                      </div>
                    )}
                    {/* Planning Deadline Indicator */}
                    {trip.financial_health?.days_until_deadline !== null && trip.financial_health?.days_until_deadline !== undefined && trip.financial_health?.days_until_deadline <= 30 && trip.financial_health?.days_until_deadline > -999 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        trip.financial_health.days_until_deadline <= 0 ? 'bg-red-50 text-red-700' :
                        trip.financial_health.days_until_deadline <= 14 ? 'bg-amber-50 text-amber-700' :
                        'bg-blue-50 text-blue-700'
                      }`} data-testid={`trip-deadline-${trip.id}`}>
                        {trip.financial_health.days_until_deadline <= 0 ? 'Past deadline' : `${trip.financial_health.days_until_deadline}d to deadline`}
                      </span>
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      trip.trip_status === 'booked' ? 'bg-[var(--vt-sage-muted)] text-[var(--vt-sage)]' :
                      trip.trip_status === 'draft' ? 'bg-[var(--vt-bg-muted)] text-[var(--vt-text-muted)]' :
                      'bg-[var(--vt-gold-muted)] text-[var(--vt-gold)]'
                    }`}>
                      {(trip.trip_status || 'draft').replace(/_/g, ' ')}
                    </span>
                    <button
                      onClick={(e) => handleDeleteTrip(e, trip.id, trip.internal_trip_name || trip.destinations)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                      disabled={deletingId === trip.id}
                      data-testid={`delete-trip-${trip.id}`}
                    >
                      {deletingId === trip.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                    <button onClick={() => navigate(`/advisor/trips/${trip.id}`)} className="p-2 hover:bg-[var(--vt-bg-muted)] rounded-lg">
                      <ChevronRight className="w-4 h-4 text-[var(--vt-text-muted)]" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {filteredTrips.length === 0 && (
              <p className="text-center py-8 text-[var(--vt-text-muted)]">No trips found</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== BOOKINGS PAGE ====================
export function BookingsPage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [trips, setTrips] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showEmailParser, setShowEmailParser] = useState(false);
  const [editBooking, setEditBooking] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bRes, tRes, cRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/travels/trips`),
        axios.get(`${API}/clients`),
      ]);
      setBookings(bRes.data);
      setTrips(tRes.data);
      setClients(cRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await axios.delete(`${API}/bookings/${id}`);
      toast.success('Booking deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete booking');
    }
  };

  const BOOKING_ICONS = {
    flight: Plane, hotel: Hotel, car: Car, activity: Calendar,
    dining: Utensils, insurance: Shield, transfer: ArrowUpRight, other: ClipboardList
  };

  const filteredBookings = filterType === 'all'
    ? bookings
    : bookings.filter(b => b.booking_type === filterType);

  const STATUS_COLORS = {
    confirmed: 'bg-[var(--vt-sage-muted)] text-[var(--vt-sage)]',
    booked: 'bg-[var(--vt-sage-muted)] text-[var(--vt-sage)]',
    researching: 'bg-[var(--vt-bg-muted)] text-[var(--vt-text-muted)]',
    quoted: 'bg-[var(--vt-gold-muted)] text-[var(--vt-gold)]',
    held: 'bg-[var(--vt-gold-muted)] text-[var(--vt-gold)]',
    requested: 'bg-[var(--vt-gold-muted)] text-[var(--vt-gold)]',
    cancelled: 'bg-[var(--vt-terra-muted)] text-[var(--vt-terra)]',
  };

  return (
    <div className="min-h-screen p-6 md:p-12" style={{ backgroundColor: 'var(--vt-bg)' }}>
      <div className="max-w-6xl mx-auto">
        <BackButton />

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-2xl text-[var(--vt-text)]">Bookings</h1>
            <p className="text-sm text-[var(--vt-text-muted)]">{bookings.length} total bookings</p>
          </div>
          <Button
            onClick={() => { setEditBooking(null); setShowForm(true); }}
            className="bg-[var(--vt-gold)] hover:bg-[var(--vt-gold)]/90 text-white"
            data-testid="add-booking-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Booking
          </Button>
          <Button
            onClick={() => setShowEmailParser(true)}
            variant="outline"
            className="ml-2"
            data-testid="parse-email-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Parse email
          </Button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['all', 'flight', 'hotel', 'transfer', 'activity', 'dining', 'insurance', 'other'].map(type => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                filterType === type
                  ? 'bg-[var(--vt-status-bg)] text-[var(--vt-status-text)]'
                  : 'bg-[var(--vt-bg-card)] text-[var(--vt-text-muted)] hover:text-[var(--vt-text)]'
              }`}
              data-testid={`booking-filter-${type}`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--vt-gold)]" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-12 bg-[var(--vt-bg-card)] rounded-xl">
            <ClipboardList className="w-12 h-12 mx-auto mb-4 text-[var(--vt-text-muted)]" />
            <p className="text-[var(--vt-text-muted)]">No bookings yet</p>
            <Button
              onClick={() => { setEditBooking(null); setShowForm(true); }}
              className="mt-4 bg-[var(--vt-gold)] text-white"
              data-testid="add-first-booking-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add First Booking
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredBookings.map(booking => {
              const Icon = BOOKING_ICONS[booking.booking_type] || ClipboardList;
              const status = booking.reservation_status || booking.status || 'researching';
              const cost = booking.actual_cost || booking.quoted_cost || booking.cost || 0;
              return (
                <div key={booking.id} className="group p-4 bg-[var(--vt-bg-card)] rounded-xl border border-[var(--vt-border)] hover:border-[var(--vt-gold)]/50 transition-all" data-testid={`booking-row-${booking.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-[var(--vt-bg-muted)] flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[var(--vt-gold)]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-[var(--vt-text)] truncate">{booking.vendor || 'No vendor'}</p>
                        <p className="text-sm text-[var(--vt-text-muted)] truncate">
                          {booking.trip_name || 'No trip'} {booking.confirmation_number ? `\u2022 ${booking.confirmation_number}` : ''} {booking.start_date ? `\u2022 ${formatDate(booking.start_date)}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {cost > 0 && <p className="font-medium text-[var(--vt-text)]">${cost.toLocaleString()}</p>}
                      <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[status] || 'bg-[var(--vt-bg-muted)] text-[var(--vt-text-muted)]'}`}>
                        {status}
                      </span>
                      <button
                        onClick={() => { setEditBooking(booking); setShowForm(true); }}
                        className="p-1.5 hover:bg-[var(--vt-bg-muted)] rounded opacity-0 group-hover:opacity-100 transition-all"
                        data-testid={`edit-booking-${booking.id}`}
                      >
                        <Edit2 className="w-3.5 h-3.5 text-[var(--vt-text-muted)]" />
                      </button>
                      <button
                        onClick={() => handleDelete(booking.id)}
                        className="p-1.5 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                        data-testid={`delete-booking-${booking.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BookingFormModal
        open={showForm}
        onClose={() => { setShowForm(false); setEditBooking(null); }}
        onSaved={fetchData}
        booking={editBooking}
        trips={trips}
        clients={clients}
      />
      <EmailParserModal
        open={showEmailParser}
        onClose={() => setShowEmailParser(false)}
        onCreated={fetchData}
        trips={trips}
        clients={clients}
      />
    </div>
  );
}

// ==================== CLIENTS PAGE (travel_clients as source of truth) ====================
export function ClientsPage() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editClient, setEditClient] = useState(null);

  useEffect(() => { fetchClients(); }, []);

  useEffect(() => {
    if (clientId) {
      fetchClientDetail(clientId);
    } else {
      setSelectedClient(null);
    }
  }, [clientId]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (err) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientDetail = async (id) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/clients/${id}`);
      setSelectedClient(res.data);
    } catch (err) {
      toast.error('Failed to load client details');
      navigate('/advisor/manage/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (id, name) => {
    if (!window.confirm(`Delete client "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success('Client deleted');
      if (clientId === id) navigate('/advisor/manage/clients');
      fetchClients();
    } catch (err) {
      toast.error('Failed to delete client');
    }
  };

  const filteredClients = clients.filter(c => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (c.full_name?.toLowerCase().includes(q)) ||
      (c.email?.toLowerCase().includes(q)) ||
      (c.phone?.includes(q))
    );
  });

  // ========== CLIENT DETAIL VIEW (uses MasterClientProfile) ==========
  if (clientId) {
    return (
      <div className="min-h-screen p-6 md:p-12" style={{ backgroundColor: 'var(--vt-bg)' }}>
        <div className="max-w-4xl mx-auto">
          <MasterClientProfile
            clientId={clientId}
            onBack={() => navigate('/advisor/manage/clients')}
          />
        </div>
      </div>
    );
  }

  // ========== CLIENT LIST VIEW ==========
  return (
    <div className="min-h-screen p-6 md:p-12" style={{ backgroundColor: 'var(--vt-bg)' }}>
      <div className="max-w-6xl mx-auto">
        <BackButton />

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-2xl text-[var(--vt-text)]">Clients</h1>
            <p className="text-sm text-[var(--vt-text-muted)]">{clients.length} client accounts</p>
          </div>
          <Button
            onClick={() => { setEditClient(null); setShowForm(true); }}
            className="bg-[var(--vt-gold)] hover:bg-[var(--vt-gold)]/90 text-white"
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Client
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--vt-text-muted)]" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, phone..."
            className="pl-10 bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]"
            data-testid="client-search"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--vt-gold)]" />
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12 bg-[var(--vt-bg-card)] rounded-xl">
            <Users className="w-12 h-12 mx-auto mb-4 text-[var(--vt-text-muted)]" />
            <p className="text-[var(--vt-text-muted)]">No clients yet</p>
            <Button
              onClick={() => { setEditClient(null); setShowForm(true); }}
              className="mt-4 bg-[var(--vt-gold)] text-white"
              data-testid="add-first-client-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add First Client
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredClients.map(client => (
              <div
                key={client.id}
                className="group p-4 bg-[var(--vt-bg-card)] rounded-xl border border-[var(--vt-border)] hover:border-[var(--vt-gold)] transition-all"
                data-testid={`client-row-${client.id}`}
              >
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => navigate(`/advisor/manage/clients/${client.id}`)}
                    className="flex items-center gap-4 flex-1 text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-[var(--vt-bg-muted)] flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-[var(--vt-stone)]" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-[var(--vt-text)]">{client.full_name}</p>
                      <p className="text-sm text-[var(--vt-text-muted)]">
                        {client.email || 'No email'} {client.trip_count ? `\u2022 ${client.trip_count} trips` : ''}
                      </p>
                    </div>
                  </button>
                  <div className="flex items-center gap-4">
                    {(client.balance_due || 0) > 0 && (
                      <span className="text-sm font-medium text-[var(--vt-terra)]" data-testid={`client-balance-${client.id}`}>
                        ${client.balance_due.toLocaleString()} due
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditClient(client); setShowForm(true); }}
                      className="p-1.5 hover:bg-[var(--vt-bg-muted)] rounded opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Edit2 className="w-3.5 h-3.5 text-[var(--vt-text-muted)]" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteClient(client.id, client.full_name); }}
                      className="p-1.5 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                    <button onClick={() => navigate(`/advisor/manage/clients/${client.id}`)} className="p-2">
                      <ChevronRight className="w-4 h-4 text-[var(--vt-text-muted)]" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {filteredClients.length === 0 && (
              <p className="text-center py-8 text-[var(--vt-text-muted)]">No clients found</p>
            )}
          </div>
        )}

        <ClientFormModal
          open={showForm}
          onClose={() => { setShowForm(false); setEditClient(null); }}
          onSaved={fetchClients}
          client={editClient}
        />
      </div>
    </div>
  );
}

// ==================== PAYMENTS PAGE ====================
export function PaymentsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ payments: [], summary: {} });
  const [trips, setTrips] = useState([]);
  const [clients, setClients] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDir, setFilterDir] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editPayment, setEditPayment] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pRes, tRes, cRes, bRes] = await Promise.all([
        axios.get(`${API}/payments`),
        axios.get(`${API}/travels/trips`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/bookings`),
      ]);
      setData(pRes.data);
      setTrips(tRes.data);
      setClients(cRes.data);
      setBookings(bRes.data);
    } catch (err) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (paymentId) => {
    try {
      await axios.post(`${API}/payments/${paymentId}/mark-paid`);
      toast.success('Payment marked as paid');
      fetchData();
    } catch (err) {
      toast.error('Failed to update payment');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await axios.delete(`${API}/payments/${id}`);
      toast.success('Payment deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete payment');
    }
  };

  const filteredPayments = data.payments.filter(p => {
    if (filterDir !== 'all' && (p.direction || 'receivable') !== filterDir) return false;
    if (filterType !== 'all' && p.payment_type !== filterType) return false;
    return true;
  });

  const s = data.summary || {};

  return (
    <div className="min-h-screen p-6 md:p-12" style={{ backgroundColor: 'var(--vt-bg)' }}>
      <div className="max-w-6xl mx-auto">
        <BackButton />

        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif text-2xl text-[var(--vt-text)]">Payments</h1>
          <Button
            onClick={() => { setEditPayment(null); setShowForm(true); }}
            className="bg-[var(--vt-gold)] hover:bg-[var(--vt-gold)]/90 text-white"
            data-testid="add-payment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Record Payment
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-[var(--vt-bg-card)] border-[var(--vt-border)]">
            <CardContent className="p-4">
              <p className="text-xs text-[var(--vt-text-muted)] uppercase">AR Total</p>
              <p className="text-xl font-serif text-[var(--vt-text)]" data-testid="ar-total">${(s.total_receivable || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-[var(--vt-bg-card)] border-[var(--vt-border)]">
            <CardContent className="p-4">
              <p className="text-xs text-[var(--vt-text-muted)] uppercase">AR Received</p>
              <p className="text-xl font-serif text-[var(--vt-sage)]" data-testid="ar-received">${(s.total_received || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-[var(--vt-bg-card)] border-[var(--vt-border)]">
            <CardContent className="p-4">
              <p className="text-xs text-[var(--vt-text-muted)] uppercase">AP Total</p>
              <p className="text-xl font-serif text-[var(--vt-text)]" data-testid="ap-total">${(s.total_payable || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-[var(--vt-bg-card)] border-[var(--vt-border)]">
            <CardContent className="p-4">
              <p className="text-xs text-[var(--vt-text-muted)] uppercase">Pending</p>
              <p className="text-xl font-serif text-[var(--vt-gold)]" data-testid="pending-count">{s.total_pending || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-[var(--vt-bg-card)] border-[var(--vt-border)]">
            <CardContent className="p-4">
              <p className="text-xs text-[var(--vt-text-muted)] uppercase">Overdue</p>
              <p className="text-xl font-serif text-[var(--vt-terra)]" data-testid="overdue-count">{s.total_overdue || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6 flex-wrap">
          <div className="flex gap-2">
            {[
              { v: 'all', l: 'All' },
              { v: 'receivable', l: 'Receivable (AR)' },
              { v: 'payable', l: 'Payable (AP)' },
            ].map(f => (
              <button
                key={f.v}
                onClick={() => setFilterDir(f.v)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  filterDir === f.v
                    ? 'bg-[var(--vt-status-bg)] text-[var(--vt-status-text)]'
                    : 'bg-[var(--vt-bg-card)] text-[var(--vt-text-muted)]'
                }`}
                data-testid={`payment-filter-dir-${f.v}`}
              >
                {f.l}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {['all', 'planning_fee', 'deposit', 'commission', 'vendor_payment', 'final_balance'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  filterType === type
                    ? 'bg-[var(--vt-status-bg)] text-[var(--vt-status-text)]'
                    : 'bg-[var(--vt-bg-card)] text-[var(--vt-text-muted)]'
                }`}
                data-testid={`payment-filter-type-${type}`}
              >
                {type === 'all' ? 'All Types' : type.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--vt-gold)]" />
          </div>
        ) : data.payments.length === 0 ? (
          <div className="text-center py-12 bg-[var(--vt-bg-card)] rounded-xl">
            <DollarSign className="w-12 h-12 mx-auto mb-4 text-[var(--vt-text-muted)]" />
            <p className="text-[var(--vt-text-muted)]">No payments recorded</p>
            <Button
              onClick={() => { setEditPayment(null); setShowForm(true); }}
              className="mt-4 bg-[var(--vt-gold)] text-white"
              data-testid="add-first-payment-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Record First Payment
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredPayments.map(payment => {
              const isAR = (payment.direction || 'receivable') === 'receivable';
              return (
                <div key={payment.id} className="group p-4 bg-[var(--vt-bg-card)] rounded-xl border border-[var(--vt-border)] hover:border-[var(--vt-gold)]/50 transition-all" data-testid={`payment-row-${payment.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isAR ? 'bg-green-50' : 'bg-orange-50'}`}>
                        {isAR ? <ArrowDownLeft className="w-4 h-4 text-green-600" /> : <ArrowUpRight className="w-4 h-4 text-orange-600" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-[var(--vt-text)] truncate">
                          {payment.description || payment.payment_type?.replace(/_/g, ' ') || 'Payment'}
                        </p>
                        <p className="text-sm text-[var(--vt-text-muted)] truncate">
                          {isAR ? 'AR' : 'AP'} {'\u2022'} {payment.payment_type?.replace(/_/g, ' ')} {payment.due_date ? `\u2022 Due: ${formatDate(payment.due_date)}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <p className="font-medium text-[var(--vt-text)]">${(payment.amount || 0).toLocaleString()}</p>
                      <span className={`text-xs px-2 py-1 rounded ${
                        payment.status === 'paid' ? 'bg-[var(--vt-sage-muted)] text-[var(--vt-sage)]' :
                        payment.status === 'overdue' ? 'bg-[var(--vt-terra-muted)] text-[var(--vt-terra)]' :
                        'bg-[var(--vt-gold-muted)] text-[var(--vt-gold)]'
                      }`}>
                        {payment.status}
                      </span>
                      {payment.status !== 'paid' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkPaid(payment.id)}
                          className="border-[var(--vt-sage)] text-[var(--vt-sage)] opacity-0 group-hover:opacity-100 transition-all"
                          data-testid={`mark-paid-${payment.id}`}
                        >
                          <Check className="w-3 h-3 mr-1" /> Paid
                        </Button>
                      )}
                      <button
                        onClick={() => { setEditPayment(payment); setShowForm(true); }}
                        className="p-1.5 hover:bg-[var(--vt-bg-muted)] rounded opacity-0 group-hover:opacity-100 transition-all"
                        data-testid={`edit-payment-${payment.id}`}
                      >
                        <Edit2 className="w-3.5 h-3.5 text-[var(--vt-text-muted)]" />
                      </button>
                      <button
                        onClick={() => handleDelete(payment.id)}
                        className="p-1.5 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                        data-testid={`delete-payment-${payment.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <PaymentFormModal
        open={showForm}
        onClose={() => { setShowForm(false); setEditPayment(null); }}
        onSaved={fetchData}
        payment={editPayment}
        trips={trips}
        clients={clients}
        bookings={bookings}
      />
    </div>
  );
}

// ==================== INVOICES PAGE ====================
export function InvoicesPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [iRes, tRes] = await Promise.all([
        axios.get(`${API}/invoices`),
        axios.get(`${API}/travels/trips`),
      ]);
      setInvoices(iRes.data);
      setTrips(tRes.data);
    } catch (err) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedTripId) {
      toast.error('Please select a trip');
      return;
    }
    setGenerating(true);
    try {
      await axios.post(`${API}/invoices/generate?trip_id=${selectedTripId}`);
      toast.success('Invoice generated');
      setShowGenerate(false);
      setSelectedTripId('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async (invoiceId) => {
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send`);
      toast.success('Invoice sent');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invoice');
    }
  };

  return (
    <div className="min-h-screen p-6 md:p-12" style={{ backgroundColor: 'var(--vt-bg)' }}>
      <div className="max-w-6xl mx-auto">
        <BackButton />

        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif text-2xl text-[var(--vt-text)]">Invoices</h1>
          <Button
            onClick={() => setShowGenerate(true)}
            className="bg-[var(--vt-gold)] hover:bg-[var(--vt-gold)]/90 text-white"
            data-testid="generate-invoice-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Generate Invoice
          </Button>
        </div>

        {/* Generate Invoice Panel */}
        {showGenerate && (
          <Card className="mb-6 bg-[var(--vt-bg-card)] border-[var(--vt-border)]">
            <CardContent className="p-6">
              <h3 className="font-medium text-[var(--vt-text)] mb-4">Generate Invoice from Trip</h3>
              <div className="flex gap-4">
                <Select value={selectedTripId} onValueChange={setSelectedTripId}>
                  <SelectTrigger className="flex-1 bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="invoice-trip-select">
                    <SelectValue placeholder="Select a trip" />
                  </SelectTrigger>
                  <SelectContent>
                    {trips.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.internal_trip_name || t.destinations} — {t.client_name || ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleGenerate}
                  disabled={generating || !selectedTripId}
                  className="bg-[var(--vt-gold)] hover:bg-[var(--vt-gold)]/90 text-white"
                  data-testid="confirm-generate-invoice"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate'}
                </Button>
                <Button variant="ghost" onClick={() => { setShowGenerate(false); setSelectedTripId(''); }}>
                  Cancel
                </Button>
              </div>
              <p className="text-xs text-[var(--vt-text-muted)] mt-3">
                Auto-generates invoice from the trip's payment records as line items.
              </p>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--vt-gold)]" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12 bg-[var(--vt-bg-card)] rounded-xl">
            <FileText className="w-12 h-12 mx-auto mb-4 text-[var(--vt-text-muted)]" />
            <p className="text-[var(--vt-text-muted)]">No invoices yet</p>
            <p className="text-sm text-[var(--vt-text-subtle)]">Generate invoices from trip payment records</p>
          </div>
        ) : (
          <div className="space-y-2">
            {invoices.map(invoice => (
              <div key={invoice.id} className="p-4 bg-[var(--vt-bg-card)] rounded-xl border border-[var(--vt-border)] hover:border-[var(--vt-gold)]/50 transition-all" data-testid={`invoice-row-${invoice.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[var(--vt-text)]">{invoice.invoice_number}</p>
                    <p className="text-sm text-[var(--vt-text-muted)]">
                      {invoice.client_name} {'\u2022'} {invoice.trip_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-medium text-[var(--vt-text)]">${(invoice.total || 0).toLocaleString()}</p>
                    <span className={`text-xs px-2 py-1 rounded ${
                      invoice.status === 'paid' ? 'bg-[var(--vt-sage-muted)] text-[var(--vt-sage)]' :
                      invoice.status === 'sent' ? 'bg-[var(--vt-gold-muted)] text-[var(--vt-gold)]' :
                      'bg-[var(--vt-bg-muted)] text-[var(--vt-text-muted)]'
                    }`}>
                      {invoice.status}
                    </span>
                    {invoice.status === 'draft' && (
                      <Button
                        size="sm"
                        onClick={() => handleSend(invoice.id)}
                        className="bg-[var(--vt-gold)] text-white"
                        data-testid={`send-invoice-${invoice.id}`}
                      >
                        <Send className="w-3 h-3 mr-1" /> Send
                      </Button>
                    )}
                  </div>
                </div>
                {/* Line items preview */}
                {invoice.line_items?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[var(--vt-border)]">
                    {invoice.line_items.slice(0, 3).map((item, i) => (
                      <div key={i} className="flex justify-between text-sm text-[var(--vt-text-muted)]">
                        <span>{item.description}</span>
                        <span>${(item.amount || 0).toLocaleString()}</span>
                      </div>
                    ))}
                    {invoice.line_items.length > 3 && (
                      <p className="text-xs text-[var(--vt-text-muted)] mt-1">+{invoice.line_items.length - 3} more items</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== FORMS PAGE ====================
export function FormsPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [trips, setTrips] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesRes, tripsRes] = await Promise.all([
        axios.get(`${API}/forms/templates`),
        axios.get(`${API}/travels/trips`)
      ]);
      setTemplates(templatesRes.data.templates);
      setTrips(tripsRes.data.filter(t => t.lifecycle_stage !== 'completed'));
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendForm = async () => {
    if (!selectedTemplate || !selectedTrip) {
      toast.error('Please select a form and trip');
      return;
    }

    setSending(true);
    try {
      const instanceRes = await axios.post(`${API}/forms/instances`, {
        template_id: selectedTemplate.id,
        trip_id: selectedTrip
      });
      const sendRes = await axios.post(`${API}/forms/instances/${instanceRes.data.id}/send`);
      toast.success('Form sent successfully!');
      const link = sendRes.data.form_link;
      navigator.clipboard?.writeText(link);
      toast.info('Form link copied to clipboard');
      setSelectedTemplate(null);
      setSelectedTrip('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send form');
    } finally {
      setSending(false);
    }
  };

  const FORM_ICONS = {
    travel_planning_agreement: FileText,
    terms_conditions: ClipboardList,
    credit_card_auth: CreditCard,
    travel_insurance: AlertCircle
  };

  const FORM_COLORS = {
    travel_planning_agreement: 'var(--vt-gold)',
    terms_conditions: 'var(--vt-stone)',
    credit_card_auth: 'var(--vt-terra)',
    travel_insurance: 'var(--vt-sage)'
  };

  return (
    <div className="min-h-screen p-6 md:p-12" style={{ backgroundColor: 'var(--vt-bg)' }}>
      <div className="max-w-6xl mx-auto">
        <BackButton />

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-2xl text-[var(--vt-text)]">Forms Library</h1>
            <p className="text-sm text-[var(--vt-text-muted)]">Deep-linked, auto-populated forms</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--vt-gold)]" />
          </div>
        ) : (
          <>
            {/* Send Form Section */}
            {selectedTemplate && (
              <Card className="mb-6 bg-[var(--vt-bg-card)] border-[var(--vt-border)]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${FORM_COLORS[selectedTemplate.id] || 'var(--vt-gold)'}20` }}
                    >
                      {(() => {
                        const Icon = FORM_ICONS[selectedTemplate.id] || FileText;
                        return <Icon className="w-5 h-5" style={{ color: FORM_COLORS[selectedTemplate.id] || 'var(--vt-gold)' }} />;
                      })()}
                    </div>
                    <div>
                      <h3 className="font-medium text-[var(--vt-text)]">Send: {selectedTemplate.short_name}</h3>
                      <p className="text-xs text-[var(--vt-text-muted)]">{selectedTemplate.type}</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Select value={selectedTrip} onValueChange={setSelectedTrip}>
                      <SelectTrigger className="flex-1 bg-[var(--vt-input-bg)] border-[var(--vt-input-border)]" data-testid="form-trip-select">
                        <SelectValue placeholder="Select a trip to bind" />
                      </SelectTrigger>
                      <SelectContent>
                        {trips.map(trip => (
                          <SelectItem key={trip.id} value={trip.id}>
                            {trip.internal_trip_name || trip.destinations} — {trip.client_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleSendForm}
                      disabled={sending || !selectedTrip}
                      className="bg-[var(--vt-gold)] hover:bg-[var(--vt-gold)]/90 text-white"
                      data-testid="send-form-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Form
                    </Button>
                    <Button variant="ghost" onClick={() => { setSelectedTemplate(null); setSelectedTrip(''); }}>
                      Cancel
                    </Button>
                  </div>
                  <p className="text-xs text-[var(--vt-text-muted)] mt-3">
                    Form will be auto-populated with client name, trip details, and include deep links.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Form Templates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map(template => {
                const Icon = FORM_ICONS[template.id] || FileText;
                const color = FORM_COLORS[template.id] || 'var(--vt-gold)';

                return (
                  <div
                    key={template.id}
                    className={`p-6 bg-[var(--vt-bg-card)] rounded-xl border transition-all ${
                      selectedTemplate?.id === template.id
                        ? 'border-[var(--vt-gold)]'
                        : 'border-[var(--vt-border)] hover:border-[var(--vt-gold)]/50'
                    }`}
                    data-testid={`form-template-${template.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Icon className="w-6 h-6" style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[var(--vt-text)]">{template.short_name}</p>
                        <p className="text-sm text-[var(--vt-text-muted)] mt-1">{template.description}</p>

                        <div className="flex items-center gap-2 mt-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            template.type === 'executable' ? 'bg-[var(--vt-gold-muted)] text-[var(--vt-gold)]' :
                            template.type === 'acknowledgment' ? 'bg-[var(--vt-sage-muted)] text-[var(--vt-sage)]' :
                            'bg-[var(--vt-bg-muted)] text-[var(--vt-text-muted)]'
                          }`}>
                            {template.type === 'executable' ? 'E-Signature Required' :
                             template.type === 'acknowledgment' ? 'Acknowledgment' :
                             'Reference Document'}
                          </span>
                        </div>

                        <div className="flex gap-2 mt-4">
                          <Button
                            size="sm"
                            onClick={() => setSelectedTemplate(template)}
                            className="bg-[var(--vt-status-bg)] text-[var(--vt-status-text)]"
                            data-testid={`send-template-${template.id}`}
                          >
                            <Send className="w-3 h-3 mr-1" /> Send
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/advisor/form/template/${template.id}`)}
                            className="border-[var(--vt-border)]"
                            data-testid={`preview-template-${template.id}`}
                          >
                            Preview
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default {
  TripsPage,
  BookingsPage,
  ClientsPage,
  PaymentsPage,
  InvoicesPage,
  FormsPage,
  BackButton
};
